<script setup>
import {useAuthStore} from '~/store/auth'
import * as yup from 'yup'
import {Form, useForm} from 'vee-validate'
import {cloneDeep} from 'lodash'
import {objectHasChanges} from '~/utils/objectUtils'
import {useNotification} from '~/composables/useNotification'
import InputField from '~/components/forms/subelements/InputField.vue'
import Button from '~/components/common/Button.vue'
import CheckField from '~/components/forms/subelements/CheckField.vue'
import Listbox from 'primevue/listbox'
import CustomField from '~/components/forms/subelements/CustomField.vue'
import {useContactApi} from '~/composables/api/useContactApi'

const requestDataFormRef = ref()
const loaded = ref(true)
const sending = ref(false)
const originalValue = ref()
const currentData = ref()
const notification = useNotification()
const contactApi = useContactApi()
const authStore = useAuthStore()

const currentUser = computed(() => {
  return authStore.user
})

const emptyRequestData = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  courses: [],
  message: '',
  terms: false
}

const requestDataSchema = yup.object({
  message: yup.string().optional().label('Observaciones'),
  terms: yup
    .boolean()
    .required()
    .isTrue('Debes aceptar la política de privavidad')
    .label('Términos y Condiciones'),
})

const {validate, setValues} = useForm({
  validationSchema: requestDataSchema,
})

const props = defineProps({
  courses: {
    type: Array,
    default: () => null
  }
})

const hasChanges = computed(() => {
  return objectHasChanges(currentData.value, originalValue.value)
})

async function doValidation() {
  setValues(currentData.value)
  const result = await validate() // Valida todos los campos

  if (!result.valid) {
    requestDataFormRef.value.validate()
  }

  return {valid: result.valid, data: cloneDeep(currentData.value)}
}

async function onSubmit() {
  try {
    if (sending.value) return

    if(currentData.value.courses.length === 0) {
      return notification.showError('Debes seleccionar, al menos, un curso')
    }

    sending.value = true

    const requestInfoData = {
      referer: window.location.href,
      firstname: currentData.value.firstname || currentUser.value.firstname,
      lastname: currentData.value.lastname || currentUser.value.lastname,
      email: currentData.value.email || currentUser.value.email,
      phone: currentData.value.phone || currentUser.value.phone,
      subject: 'Solicitud de información cursos profesionales desde Carmen Olmedo',
      message: ''
    }

    const interestedCoursesMsg = currentData.value.courses.map(course => `- (${course.id}) ${course.title}`).join('\n\n')

    requestInfoData.message = `Mensaje\n\n${currentData.value.message}. \n\nCursos en los que está interesado\n\n${interestedCoursesMsg}`

    await contactApi.send(requestInfoData)
    currentData.value = cloneDeep(emptyRequestData)

    notification.showSuccess(`La solicitud de información se ha enviado correctamente. Te responderemos en breve.`)
    sending.value = false
  } catch (error) {
    sending.value = false
    notification.showError(error)
  }
}

onMounted(async () => {
  originalValue.value = cloneDeep(emptyRequestData)
  currentData.value = cloneDeep(emptyRequestData)
  loaded.value = true
})

defineExpose({
  doValidation
})
</script>

<template>
  <Form
    v-if="loaded && currentData"
    id="requestDataForm"
    ref="requestDataFormRef"
    class="grid grid-cols-12 lg:px-8 gap-4 py-4 !px-0"
    :validation-schema="requestDataSchema"
    @submit="onSubmit"
  >
    <CustomField
      class="col-span-12 text-center form-style"
      label="¿Sobre qué cursos deseas información?"
      inputId="courses"
      name="courses"
    >
      <Listbox
        v-model="currentData.courses"
        :options="props.courses"
        multiple
        option-label="title"
        :highlightOnSelect="false"
        checkmark
        class="w-full form-style"
      />
    </CustomField>

    <InputField
      v-if="!authStore.isAuth"
      class="col-span-12"
      label="Nombre"
      inputId="firstname"
      name="firstname"
      v-model="currentData.firstname"
    />

    <InputField
      v-if="!authStore.isAuth"
      class="col-span-12"
      label="Apellidos"
      inputId="lastname"
      name="lastname"
      v-model="currentData.lastname"
    />

    <InputField
      v-if="!authStore.isAuth"
      class="col-span-12"
      type="email"
      label="Email"
      inputId="email"
      name="email"
      v-model="currentData.email"
    />

    <InputField
      v-if="!authStore.isAuth || authStore.isAuth && !currentUser.phone"
      class="col-span-12"
      type="tel"
      label="Teléfono"
      inputId="phone"
      name="phone"
      v-model="currentData.phone"
    />

    <InputField
      class="col-span-12 text-center form-style"
      label="Observaciones"
      type="textarea"
      inputId="message"
      name="message"
      v-model="currentData.message"
    />

    <CheckField
      inputId="terms"
      name="terms"
      v-model="currentData.terms"
      class="col-span-12 mb-3 text-[0.9rem] lg:text-md"
    >
      He leído y acepto la
      <NuxtLink class="underline" href="/politica-de-privacidad" target="_blank">
        Política de Privacidad
      </NuxtLink>
    </CheckField>

    <div class="col-span-12 text-center my-3 flex justify-end">
      <Button
        class="w-full"
        :disabled="!hasChanges || sending"
        :loading="sending"
        label="Enviar"
        type="submit"
      />
    </div>
  </Form>
</template>
